import React, { useEffect } from 'react'
import { bindActionCreators } from 'redux'
import { connect, useDispatch } from 'react-redux'
import { t } from 'ttag'
import pako from 'pako'
import { Map, Polygon, GoogleApiWrapper } from 'google-maps-react'
import styled from 'styled-components/macro'
import breakpoints from 'assets/stylesheet'
import {
  getRegionsAsList,
  updateSelectedFilters,
  getPartnerCoverageByRegion
} from 'store/actions/filters'
import SearchResultTitle from 'components/ui/SearchResultTitle'
import SearchResultDescription from 'components/ui/SearchResultDescription'
import SearchResultItem from 'components/ui/SearchResultItem'
import SearchResultListing from 'components/ui/SearchResultListing'
import SearchResultContent from 'components/ui/SearchResultContent'
import SearchFooter from 'components/ui/SearchFooter'
import { customMapStyles, NUTS_LEVEL_COLOR_MAPPING } from 'store/constants/constants'
import LoadingSpinner from 'components/ui/LoadingSpinner'
import PropTypes from 'prop-types'

const MapContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${props => (props.small ? '25rem' : '50rem')};
  display: ${props => (props.small ? 'none' : 'block')};
  margin-top: ${props => (props.small ? '1rem' : '0')};
  @media (max-width: ${breakpoints.bpTabletPx}) {
    display: ${props => (props.small ? 'block' : 'none')};
  }
`
const Link = styled.a`
  font-size: 14px;
  color: #003399;
  display: block;
  text-decoration: none;
`

const DisabledTitle = styled.span`
  font-size: 14px;
  color: grey;
  display: block;
  cursor: not-allowed;
  margin: 0;
`

const ResultTitle = styled.div`
  color: #003399;
  font-weight: bold;
  font-size: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 4px 0;
`
const PeriodTitle = styled.div`
  color: #003399;
  font-weight: bold;
  font-size: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 4px 0;
`
const ProgrammeResults = styled.div`
  position: relative;
  top: 5px;
  overflow-y: auto;
`
const TotalContainer = styled.div`
  margin-top: 1rem;
`
const Total = styled.span`
  padding: 0.5rem;
  font-size: 12px;
  background: #003399;
  color: #ffffff;
  box-sizing: border-box;
  margin-right: 0.5rem;
  margin-top: 0.5rem;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #001166;
  }
`
const ResultNUTSMeta = styled.div`
  color: rgb(74, 74, 74);
  font-weight: bold;
  font-size: 16px;
  padding-top: 4px;
`
const CountriesAndRegionsResultsContent = styled(SearchResultContent)`
  padding: 12px;
`
const Loading = styled.div`
  margin: 10px;
`
const DisclaimerContainer = styled.div`
  margin-top: 1rem;
  margin-bottom: 0;
`
const Disclaimer = styled.div`
  color: rgb(74, 74, 74);
  font-size: 14px;
  i {
    margin-right: 5px;
  }
`
const RelevantLinksContainer = styled.div`
  margin-top: 1rem;
  margin-bottom: 0;
`
const RelevantLinksTitle = styled.div`
  color: #003399;
  font-weight: bold;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 4px 0;
`

const LoadingContainer = () => <div />

function CountriesAndRegionsResults({
  google,
  filters,
  isPending,
  isSidebarVisible,
  searchResults: { results = [] },
  getRegionsAsList: getRegionsAsListAction,
  onlyMap
}) {
  const dispatch = useDispatch()
  const { currentSearchable } = filters
  const selectedValues = [...filters.selectedFilters[currentSearchable].options.regions]
  const { filteredPartnerCoverage } = filters
  const { nutsLevel } = filters.selectedFilters[currentSearchable].options
  const handlePaths = paths => {
    const ps = []
    const checkPath = p => {
      p.forEach(current => {
        if (Array.isArray(current) && typeof current[0].lat) {
          ps.push(current)
        } else {
          current.forEach(p2 => checkPath(p2))
        }
      })
    }
    paths.forEach(p => checkPath(p))
    return ps
  }
  const toggleInArray = (array, variable) => {
    const nut = {
      id: variable.nuts_id,
      label: variable.name,
      paths: variable.paths,
      type: variable.type,
      db_id: variable.id,
      children: variable.children,
      english_alias: variable.english_alias,
      links: variable.links
    }
    if (array.length === 0) return [nut]
    const found = array.filter(element => element.id === variable.nuts_id)
    if (found.length === 0) {
      array.push(nut)
      return array
    }
    return array.filter(element => element.id !== variable.nuts_id)
  }
  const partnerSearch = (result, types) => {
    const hash = btoa(
      pako.deflate(
        JSON.stringify({
          projects: {
            themes: {
              list: [],
              type: 'or'
            }
          },
          programmes: {
            type: [],
            available: [],
            period: []
          },
          partners: {
            type: types,
            status: [],
            countries: [],
            nuts: [result]
          },
          specific_objectives: {
            thematic_objectives: [],
            thematic_priorities: []
          },
          search: {
            list: [],
            type: null
          },
          thematic_objectives_eni: []
        }),
        {
          to: 'string'
        }
      )
    )
    const searchURL = `/partners/?hide-sidebar=true#search:${hash}`
    window.open(searchURL, '_blank')
  }
  const projectSearch = result => {
    const hash = btoa(
      pako.deflate(
        JSON.stringify({
          projects: {
            status: null,
            only_projects_with_documents: false,
            project_details: {
              start: [],
              without_start: false,
              end: [],
              without_end: false
            },
            themes: {
              list: [],
              type: 'or'
            },
            macro_regional_strategies: [],
            only_infrastructure_financed: false
          },
          programmes: {
            type: [],
            period: [],
            available: []
          },
          partners: {
            status: [],
            nuts_lead: [result],
            nuts_partner: [result],
            nuts_search_type: 'both',
            selectedAreas: {}
          },
          specific_objectives: {
            thematic_objectives: [],
            thematic_priorities: []
          },
          search: {
            list: [],
            type: null,
            fields: []
          },
          documents: {
            document_lang: [],
            languages: [],
            types: [],
            search: null
          },
          project_lang: [],
          project_desc_lang: [],
          languages: [],
          translation_languages: [],
          thematic_objectives_eni: []
        }),
        {
          to: 'string'
        }
      )
    )
    const searchURL = `/projects/?hide-sidebar=true#search:${hash}`
    window.open(searchURL, '_blank')
  }
  const setNutsFilter = async nuts => {
    const data = {
      filterValue: nuts,
      filterType: 'options',
      filterId: 'regions'
    }
    const data2 = {
      filterValue: nuts.map(n => ({ id: n.id })),
      filterType: 'programmes',
      filterId: 'nuts'
    }
    dispatch(updateSelectedFilters(data))
    dispatch(updateSelectedFilters(data2))
    dispatch(
      getPartnerCoverageByRegion({
        nuts: nuts.map(n => n.id)
      })
    )
  }
  const selectNuts = (props, marker, e) => {
    const lat = e.latLng.lat()
    const lng = e.latLng.lng()
    const params = {
      lat,
      lng,
      level: nutsLevel,
      with_links: true,
      with_child_ids: true
    }

    getRegionsAsListAction(params).then(response => {
      if (response.value.length > 0) {
        let newSelected = []
        response.value.forEach(obj => {
          newSelected = toggleInArray(selectedValues || [], obj)
        })
        setNutsFilter(newSelected)
      }
    })
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const nutsParam = urlParams.get('nuts_id')
    if (nutsParam && selectedValues.length === 0) {
      const params = {
        name: nutsParam,
        with_links: true,
        with_child_ids: true
      }
      getRegionsAsListAction(params).then(response => {
        if (response.value.length > 0) {
          let newSelected = []
          response.value.forEach(obj => {
            newSelected = toggleInArray(selectedValues || [], obj)
          })
          setNutsFilter(newSelected)
        }
      })
    }
  }, [])
  const polygons = (selectedValues || []).map(nut => {
    if (nut.paths) {
      return (
        <Polygon
          key={nut.id}
          paths={handlePaths(nut.paths)}
          strokeColor={NUTS_LEVEL_COLOR_MAPPING[nut.type]}
          strokeOpacity={0.8}
          strokeWeight={2}
          fillColor={NUTS_LEVEL_COLOR_MAPPING[nut.type]}
          fillOpacity={0.35}
          onClick={selectNuts}
        />
      )
    }
    return null
  })
  if (onlyMap) {
    return (
      <MapContainer small id="countries-map">
        <Map
          initialCenter={{
            lat: 52.519325,
            lng: 13.392709
          }}
          className="map"
          google={google}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={false}
          styles={customMapStyles}
          style={{ position: 'relative', height: '100%', width: '100%' }}
          zoom={2}
          onClick={selectNuts}
          onTilesloaded={() => {
            setTimeout(() => {
              const images = document.querySelectorAll('#countries-map img')
              images.forEach(image => {
                image.alt = 'Google Maps Image' // eslint-disable-line
              })
            }, 100)
          }}
        >
          {polygons}
          {filters.isFilteredRegionsPending && <LoadingSpinner />}
        </Map>
      </MapContainer>
    )
  }
  const resultsByNuts = {}
  const objsByNutsId = {}
  selectedValues.forEach(v => {
    resultsByNuts[v.id] = results.filter(r =>
      r.eligible_geographical_area.some(a => a === v.db_id || (v.children || []).includes(a))
    )
    objsByNutsId[v.id] = v
  })
  const uniquePeriods = []
  results.forEach(r => {
    if (uniquePeriods.find(u => u.id === r.period.id)) {
      return
    }

    uniquePeriods.push(r.period)
  })
  return (
    <>
      <SearchResultTitle>{t`Information by countries & regions`}</SearchResultTitle>
      <SearchResultDescription>{t`Enter the name of a country or region in the search field and select the appropriate option from the drop-down list.`}</SearchResultDescription>
      <SearchResultDescription>{t`Alternatively, select the geographic level on the left: NUTS0 for countries, NUTS1 for larger regions within a country, NUTS2 for smaller regions, and NUTS3 for the smallest territorial level. Then click the relevant area on the map. For example, select France at NUTS0, or choose a specific region such as Ile-de-France or Paris at a more detailed level. You can zoom in if needed.`}</SearchResultDescription>
      <SearchResultDescription>{t`Once you have selected all relevant areas using either method, click Show results to display the data.`}</SearchResultDescription>
      {isSidebarVisible && (
        <MapContainer id="countries-map">
          <Map
            initialCenter={{
              lat: 52.519325,
              lng: 13.392709
            }}
            className="map"
            google={google}
            mapTypeControl={false}
            streetViewControl={false}
            fullscreenControl={false}
            styles={customMapStyles}
            style={{ position: 'relative', height: '100%', width: '100%' }}
            zoom={4}
            onClick={selectNuts}
            onTilesloaded={() => {
              setTimeout(() => {
                const images = document.querySelectorAll('#countries-map img')
                images.forEach(image => {
                  image.alt = 'Google Maps Image' // eslint-disable-line
                })
              }, 100)
            }}
          >
            {polygons}
            {filters.isFilteredRegionsPending && <LoadingSpinner />}
          </Map>
        </MapContainer>
      )}
      {!isSidebarVisible && (
        <SearchResultListing>
          {!isPending &&
            Object.keys(resultsByNuts).map(result => (
              <SearchResultItem key={result}>
                <CountriesAndRegionsResultsContent>
                  <ResultTitle>
                    [{result}]&nbsp;
                    {objsByNutsId[result].english_alias
                      ? objsByNutsId[result].english_alias
                      : objsByNutsId[result].label}
                  </ResultTitle>
                  <ResultNUTSMeta>
                    {t`NUTS code`}: {result} - {t`Level`} {objsByNutsId[result].type}
                  </ResultNUTSMeta>
                  <TotalContainer>
                    <Total onClick={() => partnerSearch(objsByNutsId[result], ['partner', 'lead'])}>
                      {t`Partnerships`}:&nbsp;
                      {filteredPartnerCoverage[result]
                        ? filteredPartnerCoverage[result].partners
                            .toString()
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                        : null}
                    </Total>
                    <Total onClick={() => partnerSearch(objsByNutsId[result], ['lead'])}>
                      {t`Lead Partnerships`}:&nbsp;
                      {filteredPartnerCoverage[result]
                        ? filteredPartnerCoverage[result].lead_partners
                            .toString()
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                        : null}
                    </Total>
                    <Total onClick={() => projectSearch(objsByNutsId[result])}>
                      {t`Projects`}:&nbsp;
                      {filteredPartnerCoverage[result]
                        ? filteredPartnerCoverage[result].projects
                            .toString()
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                        : null}
                    </Total>
                  </TotalContainer>
                  <DisclaimerContainer>
                    <Disclaimer>{t`Click on the buttons above for further information`}</Disclaimer>
                  </DisclaimerContainer>
                  {objsByNutsId[result].links.length > 0 && (
                    <RelevantLinksContainer>
                      <RelevantLinksTitle>{t`Relevant links`}</RelevantLinksTitle>
                      {objsByNutsId[result].links.map(link => (
                        <Link key={link.id} href={link.url} target="_blank">
                          {link.description}
                        </Link>
                      ))}
                    </RelevantLinksContainer>
                  )}
                  {uniquePeriods
                    .filter(
                      p =>
                        objsByNutsId[result].type === 0 ||
                        (!p.title.startsWith('2000') && !p.title.startsWith('2007'))
                    )
                    .map(p => {
                      const programmes = resultsByNuts[result].filter(
                        prog => prog.period.id === p.id
                      )
                      if (programmes.length > 0) {
                        return (
                          <ProgrammeResults key={p.title}>
                            <PeriodTitle>
                              {t`Programmes`} {p.title}
                            </PeriodTitle>
                            {programmes.map(r2 =>
                              r2.disabled ? (
                                <DisabledTitle>{r2.title}</DisabledTitle>
                              ) : (
                                <Link key={r2.id} target="_blank" href={`/programmes/${r2.id}`}>
                                  {r2.title}
                                </Link>
                              )
                            )}
                          </ProgrammeResults>
                        )
                      }
                      return null
                    })}
                  {objsByNutsId[result].type !== 0 && (
                    <DisclaimerContainer>
                      <Disclaimer>
                        <i className="far fa-engine-warning" aria-hidden="true" />
                        {t`Programmes 2000-2006 and 2007-2013 are available at Country level only (NUTS0).`}
                      </Disclaimer>
                    </DisclaimerContainer>
                  )}
                  {resultsByNuts[result].length === 0 && (
                    <ProgrammeResults>
                      <PeriodTitle>{t`No programmes found`}</PeriodTitle>
                    </ProgrammeResults>
                  )}
                </CountriesAndRegionsResultsContent>
              </SearchResultItem>
            ))}
          {isPending && <Loading>{t`Searching ...`}</Loading>}
        </SearchResultListing>
      )}
      <SearchFooter />
    </>
  )
}

CountriesAndRegionsResults.propTypes = {
  getRegionsAsList: PropTypes.func,
  google: PropTypes.object,
  filters: PropTypes.object,
  isPending: PropTypes.bool,
  isSidebarVisible: PropTypes.bool,
  searchResults: PropTypes.shape({
    results: PropTypes.array,
    count: PropTypes.number,
    next: PropTypes.string,
    previous: PropTypes.string,
    total_pages: PropTypes.number
  }),
  onlyMap: PropTypes.bool
}

const mapStateToProps = ({ filters }) => ({
  filters
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getRegionsAsList
    },
    dispatch
  )

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  GoogleApiWrapper({
    apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    LoadingContainer,
    libraries: ['places', 'visualization']
  })(CountriesAndRegionsResults)
)
